import { closeWithPopoverMotion } from "./popover-motion.util"

export const EXCLUSIVE_POPOVER_OPEN_EVENT = "lucanto:exclusive-popover-open"

let guardInstalled = false

export function announcePopoverOpen(source, group = "global") {
  closeOpenExclusivePopovers(source)
  document.dispatchEvent(
    new CustomEvent(EXCLUSIVE_POPOVER_OPEN_EVENT, {
      detail: { source, group }
    })
  )
}

export function bindExclusivePopover(source, close, group = "global") {
  const closePeerPopover = (event) => {
    if (event.detail?.group !== group) return
    if (event.detail?.source === source) return

    close()
  }

  document.addEventListener(EXCLUSIVE_POPOVER_OPEN_EVENT, closePeerPopover)

  return () => document.removeEventListener(EXCLUSIVE_POPOVER_OPEN_EVENT, closePeerPopover)
}

export function installExclusivePopoverGuard() {
  if (guardInstalled) return

  guardInstalled = true
  document.addEventListener("pointerdown", handleExclusivePopoverTrigger, true)
  document.addEventListener("click", handleExclusivePopoverTrigger, true)
  document.addEventListener("toggle", handleExclusivePopoverToggle, true)
}

function handleExclusivePopoverTrigger(event) {
  const source = popoverSourceForTrigger(event.target)
  if (source) {
    closeOpenExclusivePopovers(source)
    return
  }

  if (event.type !== "pointerdown") return
  if (isInsideOpenExclusivePopover(event.target)) return

  closeOpenExclusivePopovers(null)
}

function handleExclusivePopoverToggle(event) {
  const source = popoverSourceForOpenElement(event.target)
  if (!source) return

  closeOpenExclusivePopovers(source)
}

function popoverSourceForTrigger(target) {
  if (!(target instanceof Element)) return null

  const sort = target.closest("details.lc-table-sort")
  if (sort) return sort

  const savedViewsTrigger = target.closest("[data-action*='table-saved-views#toggle']")
  if (savedViewsTrigger) return savedViewsTrigger.closest("[data-controller~='table-saved-views']") || savedViewsTrigger

  const filterTrigger = target.closest("[data-action*='invoices-filter#openFilterMenu']")
  if (filterTrigger) return filterTrigger.closest("[data-controller~='invoices-filter']") || filterTrigger

  const bankFilterTrigger = target.closest("[data-action*='bank--transaction-filter-menu#openFilterMenu']")
  if (bankFilterTrigger) {
    return bankFilterTrigger.closest("[data-controller~='bank--transaction-filter-menu']") || bankFilterTrigger
  }

  const expenseFilterTrigger = target.closest("[data-action*='expenses--filter-menu#openFilterMenu']")
  if (expenseFilterTrigger) {
    return expenseFilterTrigger.closest("[data-controller~='expenses--filter-menu']") || expenseFilterTrigger
  }

  const columnsTrigger = target.closest("[data-action*='table-interactions#toggleViewSettings']")
  if (columnsTrigger) return columnsTrigger.closest("[data-controller~='table-interactions']") || columnsTrigger

  return null
}

function popoverSourceForOpenElement(target) {
  if (!(target instanceof Element)) return null

  if (target.matches("details[open].lc-table-sort")) return target
  if (target.matches("[popover]") && isPopoverOpen(target)) {
    return (
      target.closest("[data-controller~='invoices-filter']") ||
      target.closest("[data-controller~='bank--transaction-filter-menu']") ||
      target.closest("[data-controller~='expenses--filter-menu']") ||
      target
    )
  }

  return null
}

function closeOpenExclusivePopovers(source) {
  closeOpenDetails(source)
  closeHiddenPanels(source)
  closeNativePopovers(source)
}

function isInsideOpenExclusivePopover(target) {
  if (!(target instanceof Element)) return false

  if (target.closest("details[open].lc-table-sort")) return true
  if (target.closest(".lc-table-saved-views__menu:not([hidden]), .lc-table-view-settings:not([hidden])")) return true

  const nativePopover = target.closest("[popover]")
  return Boolean(nativePopover && isPopoverOpen(nativePopover))
}

function closeOpenDetails(source) {
  document.querySelectorAll("details[open].lc-table-sort").forEach((details) => {
    if (isSourceElement(details, source)) return

    closeWithPopoverMotion(details.querySelector(".lc-table-sort__menu"), () => {
      details.open = false
    })
  })
}

function closeHiddenPanels(source) {
  document
    .querySelectorAll(".lc-table-saved-views__menu:not([hidden]), .lc-table-view-settings:not([hidden])")
    .forEach((panel) => {
      if (isSourceElement(panel, source)) return

      closeWithPopoverMotion(panel, () => {
        panel.hidden = true
      })
      syncPanelTriggerState(panel)
    })
}

function closeNativePopovers(source) {
  document.querySelectorAll("[popover]").forEach((popover) => {
    if (isSourceElement(popover, source)) return
    if (!isPopoverOpen(popover)) return

    closeWithPopoverMotion(popover, () => {
      popover.hidePopover()
    })
    syncNativePopoverTriggerState(popover)
  })
}

// The one way to ask whether a native popover is open. A browser without the
// Popover API (Chrome before 114) rejects `:popover-open` as an invalid
// selector and `matches` throws; callers ask from document-wide pointer and
// key listeners, so a bare `matches` there throws on every click (LUC-901).
export function isPopoverOpen(popover) {
  if (!popover) return false

  try {
    return popover.matches(":popover-open")
  } catch (_error) {
    return false
  }
}

function isSourceElement(element, source) {
  if (!(source instanceof Element)) return false

  return element === source || element.contains(source) || source.contains(element)
}

function syncPanelTriggerState(panel) {
  const tableController = panel.closest("[data-controller~='table-interactions']")
  if (tableController) {
    tableController.querySelectorAll("[data-table-interactions-target~='viewSettingsButton']").forEach((button) => {
      button.setAttribute("aria-expanded", "false")
      button.classList.remove("is-active")
    })
    tableController.querySelectorAll("[data-table-interactions-target~='addColumnPanel']").forEach((addPanel) => {
      addPanel.hidden = true
    })
  }

  const savedViewsController = panel.closest("[data-controller~='table-saved-views']")
  const savedViewsButton = savedViewsController?.querySelector("[data-table-saved-views-target~='button']")
  if (savedViewsButton) {
    savedViewsButton.setAttribute("aria-expanded", "false")
    savedViewsButton.classList.remove("is-active")
  }
}

function syncNativePopoverTriggerState(popover) {
  const filterController = popover.closest("[data-controller~='invoices-filter']")
  const filterButton = filterController?.querySelector("[data-invoices-filter-target~='filterButton']")
  filterButton?.classList.remove("filter-button--active")

  const bankFilterController = popover.closest("[data-controller~='bank--transaction-filter-menu']")
  const bankFilterButton = bankFilterController?.querySelector(
    "[data-bank--transaction-filter-menu-target~='filterButton']"
  )
  bankFilterButton?.classList.remove("filter-button--active")

  const expenseFilterController = popover.closest("[data-controller~='expenses--filter-menu']")
  const expenseFilterButton = expenseFilterController?.querySelector(
    "[data-expenses--filter-menu-target~='filterButton']"
  )
  expenseFilterButton?.classList.remove("filter-button--active")
}
